import Chart from "chart.js/auto";
import { fetchEvents, fetchMovements, fetchProducts } from "../lib/api.js";
import { computeInsights, profitByProductByMonth, quarterComparison } from "../lib/insights.js";
import { lastMonths, eventsInLastMonths, sum } from "../lib/aggregate.js";
import { formatCompact, formatCurrency, formatDate, monthLabel } from "../lib/format.js";
import { escapeHtml, showError } from "../lib/ui.js";

const SEVERITY_COLOR = { alta: "#e0554c", media: "#e0a44c", baja: "#6b7280" };
const SEVERITY_LABEL = { alta: "urgente", media: "atención", baja: "menor" };

export async function renderInsights(container) {
  container.innerHTML = `<h2>Alertas</h2><p>Analizando los datos…</p>`;

  try {
    const [events, movements, products] = await Promise.all([fetchEvents(), fetchMovements(), fetchProducts()]);
    const findings = computeInsights(events, movements, products);
    const quarters = quarterComparison(events);
    const monthComparison = compareMonths(events);

    container.innerHTML = `
      <h2>Alertas</h2>
      <p style="color: var(--text-muted); margin-top: -12px">
        Todo lo que ves acá sale de tus propios eventos y movimientos. Cada alerta se puede abrir para ver los números exactos con los que se calculó.
      </p>

      <div class="cards-grid">
        ${comparisonCard("Este mes vs. el anterior", monthComparison)}
        ${comparisonCard("Este trimestre vs. el anterior", quarters)}
      </div>

      <div class="panel">
        <h3>Qué conviene mirar (${findings.length})</h3>
        ${
          findings.length === 0
            ? "<p>No detectamos problemas en los datos cargados. Cuantos más eventos cargues, más precisas se vuelven las alertas.</p>"
            : findings.map(findingHtml).join("")
        }
      </div>

      <div class="panel">
        <h3>Ganancia por producto, mes a mes</h3>
        <div class="chart-wrap"><canvas id="insights-chart"></canvas></div>
      </div>
    `;

    container.querySelectorAll("[data-toggle]").forEach((btn) =>
      btn.addEventListener("click", () => {
        const detail = container.querySelector(`#detail-${CSS.escape(btn.dataset.toggle)}`);
        detail.hidden = !detail.hidden;
        btn.textContent = detail.hidden ? "Ver los números" : "Ocultar los números";
      })
    );

    drawProductTrendChart(container.querySelector("#insights-chart"), events, products);

    // Bloque nuevo de Colonia y Empresa (tanda 2), debajo de todo y con
    // consultas propias. Con el interruptor apagado no agrega nada.
    import("./colonia/bloques.js").then((m) => m.mountAlertasBloque(container)).catch(() => {});
  } catch (error) {
    showError(container, error);
  }
}

function findingHtml(finding) {
  const color = SEVERITY_COLOR[finding.severity];
  return `
    <div style="border-left: 4px solid ${color}; padding: 4px 0 4px 14px; margin-bottom: 22px">
      <div style="display:flex; gap:10px; align-items:center; flex-wrap:wrap; margin-bottom:6px">
        <span class="badge" style="background:${color}">${SEVERITY_LABEL[finding.severity]}</span>
        <strong>${escapeHtml(finding.title)}</strong>
      </div>
      <p style="margin: 6px 0">${escapeHtml(finding.message)}</p>
      <p style="margin: 6px 0; background: var(--bg); padding: 10px 12px; border-radius: 8px">
        <strong>Qué hacer:</strong> ${escapeHtml(finding.recommendation)}
      </p>
      <button class="icon-btn" data-toggle="${escapeHtml(finding.id)}">Ver los números</button>
      <div id="detail-${escapeHtml(finding.id)}" hidden style="margin-top: 10px">
        ${evidenceTable(finding.evidence)}
      </div>
    </div>
  `;
}

const COLUMN_LABELS = {
  date: "fecha",
  description: "evento",
  income: "ingreso",
  cost: "costos",
  profit: "ganancia",
  month: "mes",
};

function evidenceTable(evidence) {
  if (!evidence || evidence.length === 0) return "";
  const columns = Object.keys(evidence[0]);

  return `<div class="table-wrap"><table>
    <thead><tr>${columns
      .map((c) => `<th>${escapeHtml(COLUMN_LABELS[c] ?? c.replaceAll("_", " "))}</th>`)
      .join("")}</tr></thead>
    <tbody>
      ${evidence
        .map(
          (row) =>
            `<tr>${columns
              .map((c) => {
                const value = row[c];
                const isMoney = ["income", "cost", "profit", "ingreso", "egreso", "balance", "total"].includes(c);
                if (isMoney) return `<td>${escapeHtml(formatCurrency(value))}</td>`;
                if (c === "date") return `<td>${escapeHtml(formatDate(value))}</td>`;
                return `<td>${escapeHtml(value)}</td>`;
              })
              .join("")}</tr>`
        )
        .join("")}
    </tbody>
  </table></div>`;
}

function comparisonCard(title, { current, previous }) {
  const delta = previous.profit === 0 ? null : ((current.profit - previous.profit) / Math.abs(previous.profit)) * 100;
  const better = current.profit >= previous.profit;

  return `
    <div class="card">
      <div class="label">${escapeHtml(title)}</div>
      <div class="value ${better ? "positive" : "negative"}">${formatCurrency(current.profit)}</div>
      <div class="label" style="margin-top: 8px">
        Antes: ${formatCurrency(previous.profit)}${delta === null ? "" : ` · ${delta >= 0 ? "+" : ""}${Math.round(delta)}%`}
      </div>
      <div class="label">${current.events} evento(s) vs. ${previous.events} antes</div>
    </div>
  `;
}

function compareMonths(events) {
  const now = new Date();
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const currentEvents = eventsInLastMonths(events, 1, now);
  const previousEvents = eventsInLastMonths(events, 1, prev);

  const totals = (list) => ({
    events: list.length,
    income: sum(list, (e) => e.income),
    profit: sum(list, (e) => e.profit),
  });

  return { current: totals(currentEvents), previous: totals(previousEvents) };
}

function drawProductTrendChart(canvas, events, products) {
  const months = lastMonths(6);
  const series = profitByProductByMonth(events, products, 6);

  new Chart(canvas, {
    type: "line",
    data: {
      labels: months.map((m) => monthLabel(m.year, m.month)),
      datasets: series.map((s) => ({
        label: s.product.name,
        data: s.series,
        borderColor: s.product.color,
        backgroundColor: s.product.color,
        tension: 0.3,
      })),
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          beginAtZero: true,
          // En pesos el eje llega a los millones: sin abreviar, las etiquetas
          // se comen el ancho del gráfico.
          ticks: { callback: (value) => formatCompact(value) },
        },
      },
      plugins: {
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${formatCurrency(ctx.parsed.y)}`,
          },
        },
      },
    },
  });
}
